import React, { useState } from "react";
import Sidebar from "../Sidebar";
import Layout from "../layout/Layout";
import { useQuery, useMutation, gql, useLazyQuery } from "@apollo/client";
import Loader from "../../Loader";
import OutOfHoursBonusEditor from "./OutOfHoursBonusEditor";
import ConfigInfo from "./ConfigInfo";
import SettingsHistoryModal from "./SettingsHistoryModal";
import RuleAcknowledgementsModal from "./RuleAcknowledgementsModal";
import PayrollCheckModal from "./PayrollCheckModal";
import WhatIfModal, { WHAT_IF_KEYS } from "./WhatIfModal";
import PayrollTrends from "./PayrollTrends";
import OrderDetailsModal from "../AdminBookings/OrderDetailsModal";
import { GET_BOOKINGS } from "../AdminBookings/graphql";
import AddSupplementaryModal from "./AddSupplementaryModal";
import FullFinalList from "./FullFinalList";

// Config values are JSON; objects and arrays can't be rendered as-is
const showConfigValue = (value) =>
  value !== null && typeof value === "object" ? JSON.stringify(value) : String(value ?? "");

// Queries
const GET_PAYROLL_RUNS = gql`
  query GetPayrollRuns {
    getPayrollRuns {
      status
      message
      payrollRuns {
        id
        month
        year
        expectedWorkingDays
        status
        createdAt
      }
    }
  }
`;

const GET_EMPLOYEE_PAYOUTS = gql`
  query GetEmployeePayouts($payrollRunId: ID!) {
    getEmployeePayouts(payrollRunId: $payrollRunId) {
      status
      message
      payouts {
        id
        payrollRunId
        employeeId
        baseSalary
        commission
        leaveDeduction
        adjustmentTotal
        netPayout
        status
        carriedForward
        cashAdjusted
        transferAmount
        supplementary
        supplementaryReason
        baseCommission
        bonusCommission
        travelCommission
        paidAt
        paymentReference
        paymentMode
        employee {
          id
          fullName
          contactNumber
        }
        adjustments {
          id
          amount
          type
          subCategory
          adjustmentDirection
          date
          description
          createdAt
        }
      }
    }
  }
`;

const GET_RESTRICTED_DAYS = gql`
  query GetRestrictedDays {
    getRestrictedDays {
      status
      message
      restrictedDays {
        id
        date
        name
        kind
      }
    }
  }
`;

const GET_SYSTEM_CONFIGS = gql`
  query GetSystemConfigs {
    getSystemConfigs {
      status
      message
      configs {
        id
        key
        valueJson
        area
        unit
        description
        defaultValue
        inUse
        isDefault
      }
    }
  }
`;

const GET_EMPLOYEES = gql`
  query GetEmployeelist($data: EmployeeFilterInput) {
    getEmployeelist(data: $data) {
      status
      employee {
        id
        fullName
        contactNumber
      }
    }
  }
`;

// The bookings behind a payout's commission, counted exactly as payroll
// counts them (IST month, verified + payment-pending)
const GET_EMPLOYEE_MONTH_COMMISSIONS = gql`
  query GetEmployeeMonthCommissions($employeeId: ID!, $month: Int!, $year: Int!) {
    getEmployeeMonthCommissions(employeeId: $employeeId, month: $month, year: $year) {
      status
      message
      bookings {
        orderId
        bookingId
        date
        price
        baseCommission
        bonusCommission
        travelCommission
        totalCommission
      }
    }
  }
`;

// Mutations
const GENERATE_PAYROLL = gql`
  mutation GeneratePayroll($month: Int!, $year: Int!) {
    generatePayroll(month: $month, year: $year) {
      status
      message
    }
  }
`;


const MARK_PAYROLL_PAID = gql`
  mutation MarkPayrollPaid($payrollRunId: ID!, $paymentReference: String, $paymentMode: String, $adjustCash: Boolean) {
    markPayrollPaid(payrollRunId: $payrollRunId, paymentReference: $paymentReference, paymentMode: $paymentMode, adjustCash: $adjustCash) {
      status
      message
    }
  }
`;

const MARK_PAYOUT_PAID = gql`
  mutation MarkPayoutPaid($payoutId: ID!, $paymentReference: String, $paymentMode: String!, $adjustCash: Boolean) {
    markPayoutPaid(payoutId: $payoutId, paymentReference: $paymentReference, paymentMode: $paymentMode, adjustCash: $adjustCash) {
      status
      message
    }
  }
`;

// Booking cash the professional still holds - optionally set against the pay
const GET_CASH_IN_HAND = gql`
  query GetEmployeeCashInHand($employeeId: ID!) {
    getEmployeeCashInHand(employeeId: $employeeId) {
      status
      message
      cash {
        amount
        bookings {
          orderId
          bookingId
          date
          amount
        }
      }
    }
  }
`;

// One row per professional for accounts / the CA
const downloadRegister = (run, payouts) => {
  const cols = [
    ["Professional", (p) => p.employee?.fullName],
    ["Phone", (p) => p.employee?.contactNumber],
    ["Salary", (p) => p.baseSalary],
    ["Base commission", (p) => p.baseCommission],
    ["Out-of-hours bonus", (p) => p.bonusCommission],
    ["Commission (base + out-of-hours)", (p) => (p.commission || 0) - (p.travelCommission || 0)],
    ["Travel allowance", (p) => p.travelCommission],
    ["Leave deductions", (p) => p.leaveDeduction],
    ["Adjustments", (p) => p.adjustmentTotal],
    ["Net pay", (p) => p.netPayout],
    ["Carried to next month", (p) => p.carriedForward],
    ["Cash set against pay", (p) => p.cashAdjusted],
    ["Transferred", (p) => (p.transferAmount ?? (p.status === "PAID" ? p.netPayout : ""))],
    ["Status", (p) => (p.supplementary && p.status === "DRAFT" ? "WAITING_APPROVAL" : p.status)],
    ["Paid on", (p) => (p.paidAt ? new Date(p.paidAt).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" }) : "")],
    ["Mode", (p) => p.paymentMode],
    ["Reference", (p) => p.paymentReference],
    ["Added after lock", (p) => (p.supplementary ? `Yes - ${p.supplementaryReason || ""}` : "")],
  ];
  const esc = (v) => {
    const t = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
  };
  const csv = [cols.map(([h]) => h).join(","), ...payouts.map((p) => cols.map(([, f]) => esc(f(p))).join(","))].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `payroll-register_${run.year}-${String(run.month).padStart(2, "0")}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const PAYMENT_MODES = [
  ["BANK_TRANSFER", "Bank transfer"],
  ["UPI", "UPI"],
  ["CASH", "Cash"],
  ["OTHER", "Other"],
];

const ADD_RESTRICTED_DAY = gql`
  mutation AddRestrictedDay($date: String!, $name: String, $kind: String) {
    addRestrictedDay(date: $date, name: $name, kind: $kind) {
      status
      message
    }
  }
`;

const REMOVE_RESTRICTED_DAY = gql`
  mutation RemoveRestrictedDay($id: ID!) {
    removeRestrictedDay(id: $id) {
      status
      message
    }
  }
`;

const UPDATE_SYSTEM_CONFIG = gql`
  mutation UpdateSystemConfig($key: String!, $valueJson: JSON!) {
    updateSystemConfig(key: $key, valueJson: $valueJson) {
      status
      message
    }
  }
`;

const ADD_EMPLOYEE_ADJUSTMENT = gql`
  mutation AddEmployeeAdjustment(
    $employeeId: String!
    $type: String!
    $amount: Int!
    $adjustmentDirection: String!
    $description: String
    $date: String
    $subCategory: String
    $bookingRef: String
  ) {
    addEmployeeAdjustment(
      employeeId: $employeeId
      type: $type
      amount: $amount
      adjustmentDirection: $adjustmentDirection
      description: $description
      date: $date
      subCategory: $subCategory
      bookingRef: $bookingRef
    ) {
      status
      message
    }
  }
`;

// SOP deduction / feedback penalty and their sub-types
const GET_ADJUSTMENT_CATEGORIES = gql`
  query GetAdjustmentCategories {
    getAdjustmentCategories {
      type
      label
      subCategories {
        key
        label
      }
    }
  }
`;

const GET_EMPLOYEE_PENALTIES = gql`
  query GetEmployeePenalties($employeeId: String!, $month: Int!, $year: Int!) {
    getEmployeePenalties(employeeId: $employeeId, month: $month, year: $year) {
      status
      message
      penalties {
        id
        date
        penaltyAmount
        dayCharge
        reason
        status
      }
    }
  }
`;

const UPDATE_LEAVE_PENALTY_STATUS = gql`
  mutation UpdateLeavePenaltyStatus($id: ID!, $status: String!) {
    updateLeavePenaltyStatus(id: $id, status: $status) {
      status
      message
    }
  }
`;

// DateTime fields come as epoch-ms strings or ISO strings
const toDate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const d = /^\d+$/.test(String(value)) ? new Date(parseInt(value, 10)) : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDate = (dateStr) => {
  const d = toDate(dateStr);
  return d ? d.toLocaleDateString() : "-";
};

const formatDateTime = (value) => {
  const d = toDate(value);
  return d ? d.toLocaleString() : "-";
};

const PayoutDashboard = () => {
  const [activeTab, setActiveTab] = useState("runs");
  const [showRunModal, setShowRunModal] = useState(false);
  const [showPayoutsModal, setShowPayoutsModal] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);

  // Create Run Form
  const [runMonth, setRunMonth] = useState(new Date().getMonth() + 1);
  const [runYear, setRunYear] = useState(new Date().getFullYear());
  // Expected working days are calculated by the backend (every day of the
  // month - there is no weekly off; generateMonthlyPayroll in payout.util.js);
  // shown here as a read-only preview using the same rule.
  const expectedWorkingDays = (() => {
    const m = parseInt(runMonth);
    const y = parseInt(runYear);
    if (!m || !y) return null;
    return new Date(y, m, 0).getDate();
  })();

  // Restricted Days Form
  const [restrictedDate, setRestrictedDate] = useState("");
  const [restrictedName, setRestrictedName] = useState("");
  // { entityType, entityKey?, title } while a change-history modal is open
  const [historyFor, setHistoryFor] = useState(null);
  const [showAcks, setShowAcks] = useState(false);
  // { run, lock } - lock: open from "Lock Run" with a Lock now button
  const [checkFor, setCheckFor] = useState(null);
  const [whatIfFor, setWhatIfFor] = useState(null);
  const [showAddMissed, setShowAddMissed] = useState(false);
  // RESTRICTED, or NORMAL = a Saturday / Sunday charged like a weekday
  const [restrictedKind, setRestrictedKind] = useState("RESTRICTED");

  // Edit Config State
  const [editingConfig, setEditingConfig] = useState(null);
  const [editingConfigValue, setEditingConfigValue] = useState("");

  // Adjustments Modal State
  const [showAdjustmentModal, setShowAdjustmentModal] = useState(false);
  const [adjustmentEmployeeId, setAdjustmentEmployeeId] = useState("");
  const [adjustmentAmount, setAdjustmentAmount] = useState("");
  const [adjustmentType, setAdjustmentType] = useState("BONUS");
  const [adjustmentReason, setAdjustmentReason] = useState("");
  const [adjustmentDate, setAdjustmentDate] = useState("");
  const [adjustmentSub, setAdjustmentSub] = useState("");
  const [adjustmentBooking, setAdjustmentBooking] = useState("");
  const { data: categoriesData } = useQuery(GET_ADJUSTMENT_CATEGORIES);
  const subTyped = (categoriesData?.getAdjustmentCategories || []).find((c) => c.type === adjustmentType);

  // Penalty Breakdown State
  const [showPenaltyModal, setShowPenaltyModal] = useState(false);
  const [selectedPenaltyEmployee, setSelectedPenaltyEmployee] = useState(null);

  // Adjustments Details State
  const [showAdjustmentsModal, setShowAdjustmentsModal] = useState(false);
  const [selectedAdjustments, setSelectedAdjustments] = useState([]);
  const [selectedAdjustmentEmployee, setSelectedAdjustmentEmployee] = useState(null);

  // Commissions Details State
  const [showCommissionsModal, setShowCommissionsModal] = useState(false);
  const [selectedCommissionEmployee, setSelectedCommissionEmployee] = useState(null);

  // Queries
  const {
    data: runsData,
    loading: runsLoading,
    refetch: refetchRuns,
  } = useQuery(GET_PAYROLL_RUNS, {
    fetchPolicy: "network-only",
  });
  const {
    data: restrictedData,
    loading: restrictedLoading,
    refetch: refetchRestricted,
  } = useQuery(GET_RESTRICTED_DAYS, {
    fetchPolicy: "network-only",
  });
  const {
    data: configsData,
    loading: configsLoading,
    refetch: refetchConfigs,
  } = useQuery(GET_SYSTEM_CONFIGS, {
    fetchPolicy: "network-only",
  });
  const { data: empData } = useQuery(GET_EMPLOYEES, {
    variables: { data: {} },
    fetchPolicy: "network-only",
  });

  const { data: payoutsData, refetch: refetchPayouts } = useQuery(
    GET_EMPLOYEE_PAYOUTS,
    {
      variables: { payrollRunId: selectedRun?.id || "" },
      skip: !selectedRun?.id,
      fetchPolicy: "network-only",
    },
  );

  // Mutations
  const [generatePayroll] = useMutation(GENERATE_PAYROLL);
  const [markPayrollPaid] = useMutation(MARK_PAYROLL_PAID);
  const [markPayoutPaid] = useMutation(MARK_PAYOUT_PAID);
  // { kind: "run", run } or { kind: "payout", payout } - asks for proof
  const [payingFor, setPayingFor] = useState(null);
  const [payRef, setPayRef] = useState("");
  const [payMode, setPayMode] = useState("BANK_TRANSFER");
  const [adjustCash, setAdjustCash] = useState(false);
  const [loadCash, { data: cashData, loading: cashLoading }] = useLazyQuery(GET_CASH_IN_HAND, {
    fetchPolicy: "network-only",
  });
  const cashInHand = cashData?.getEmployeeCashInHand?.cash;
  const [addRestrictedDay] = useMutation(ADD_RESTRICTED_DAY);
  const [removeRestrictedDay] = useMutation(REMOVE_RESTRICTED_DAY);
  const [updateSystemConfig] = useMutation(UPDATE_SYSTEM_CONFIG);
  const [addEmployeeAdjustment] = useMutation(ADD_EMPLOYEE_ADJUSTMENT);
  const [updateLeavePenaltyStatus] = useMutation(UPDATE_LEAVE_PENALTY_STATUS);

  const [getMonthCommissions, { data: commissionsData, loading: bookingsLoading }] = useLazyQuery(
    GET_EMPLOYEE_MONTH_COMMISSIONS,
    { fetchPolicy: "network-only" },
  );
  const commissionRows = commissionsData?.getEmployeeMonthCommissions?.bookings || [];

  // Booking ID links open the booking details dialog
  const [fetchBooking] = useMutation(GET_BOOKINGS);
  const [viewingBooking, setViewingBooking] = useState(null);
  const [openingBookingId, setOpeningBookingId] = useState(null);
  const openBooking = async (orderId, bookingId) => {
    setOpeningBookingId(orderId);
    try {
      const res = await fetchBooking({ variables: { data: { id: orderId, bookingId } } });
      const orders = res.data?.getBookings?.orders || [];
      const order = orders.find((o) => String(o.id) === String(orderId));
      if (!order) throw new Error(res.data?.getBookings?.message || "Booking not found");
      setViewingBooking(order);
    } catch (e) {
      alert(`Could not open the booking: ${e.message}`);
    } finally {
      setOpeningBookingId(null);
    }
  };

  const [getEmployeePenalties, { data: penaltiesData, loading: penaltiesLoading, refetch: refetchPenalties }] = useLazyQuery(GET_EMPLOYEE_PENALTIES, {
    fetchPolicy: "network-only",
  });

  const handleCreateRunSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await generatePayroll({
        variables: {
          month: parseInt(runMonth),
          year: parseInt(runYear),
        },
      });
      if (res.data.generatePayroll.status === "SUCCESS") {
        setShowRunModal(false);
        refetchRuns();
      } else {
        alert(res.data.generatePayroll.message);
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleMarkPaid = (run) => {
    setPayRef("");
    setPayMode("BANK_TRANSFER");
    setAdjustCash(false);
    setPayingFor({ kind: "run", run });
  };

  const submitPayment = async () => {
    try {
      let out;
      if (payingFor.kind === "run") {
        const res = await markPayrollPaid({
          variables: { payrollRunId: payingFor.run.id, paymentReference: payRef, paymentMode: payMode, adjustCash },
        });
        out = res.data.markPayrollPaid;
      } else {
        const res = await markPayoutPaid({
          variables: { payoutId: payingFor.payout.id, paymentReference: payRef, paymentMode: payMode, adjustCash },
        });
        out = res.data.markPayoutPaid;
        if (out.status === "SUCCESS" && adjustCash) alert(out.message);
      }
      if (out.status === "SUCCESS") {
        setPayingFor(null);
        refetchRuns();
        if (selectedRun?.id) refetchPayouts();
      } else {
        alert(out.message);
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleAddRestrictedSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await addRestrictedDay({
        variables: { date: restrictedDate, name: restrictedName, kind: restrictedKind },
      });
      if (res.data.addRestrictedDay.status === "SUCCESS") {
        setRestrictedDate("");
        setRestrictedName("");
        setRestrictedKind("RESTRICTED");
        refetchRestricted();
      } else {
        alert(res.data.addRestrictedDay.message);
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleRemoveRestricted = async (id) => {
    if (!window.confirm("Are you sure you want to remove this restricted day?"))
      return;
    try {
      const res = await removeRestrictedDay({ variables: { id } });
      if (res.data.removeRestrictedDay.status === "SUCCESS") {
        refetchRestricted();
      } else {
        alert(res.data.removeRestrictedDay.message);
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleUpdateConfigSubmit = async (e) => {
    e.preventDefault();
    try {
      let parsedVal;
      try {
        parsedVal = JSON.parse(editingConfigValue);
      } catch {
        parsedVal = editingConfigValue; // fallback to raw string if it is not valid JSON
      }
      const res = await updateSystemConfig({
        variables: { key: editingConfig.key, valueJson: parsedVal },
      });
      if (res.data.updateSystemConfig.status === "SUCCESS") {
        setEditingConfig(null);
        setEditingConfigValue("");
        refetchConfigs();
      } else {
        alert(res.data.updateSystemConfig.message);
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const handleAddAdjustmentSubmit = async (e) => {
    e.preventDefault();
    try {
      const debitTypes = [
        "MANUAL_DEDUCTION",
        "TOOLS_CHARGES",
        "ADVANCE_PAYMENT",
        "TRAINING_CHARGES",
        "SOP_DEDUCTION",
        "FEEDBACK_PENALTY",
      ];
      const direction = debitTypes.includes(adjustmentType)
        ? "DEBIT"
        : "CREDIT";
      const res = await addEmployeeAdjustment({
        variables: {
          employeeId: adjustmentEmployeeId,
          type: adjustmentType,
          amount: Math.round(parseFloat(adjustmentAmount)),
          adjustmentDirection: direction,
          description: adjustmentReason,
          date: adjustmentDate || null,
          subCategory: subTyped ? adjustmentSub || null : null,
          bookingRef: subTyped ? adjustmentBooking.trim() || null : null,
        },
      });
      if (res.data.addEmployeeAdjustment.status === "SUCCESS") {
        setShowAdjustmentModal(false);
        setAdjustmentEmployeeId("");
        setAdjustmentAmount("");
        setAdjustmentReason("");
        setAdjustmentDate("");
        setAdjustmentSub("");
        setAdjustmentBooking("");
        if (selectedRun?.id) {
          refetchPayouts();
        }
      } else {
        alert(res.data.addEmployeeAdjustment.message);
      }
    } catch (err) {
      alert(err.message);
    }
  };

  const getMonthName = (num) => {
    const dates = [
      "",
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return dates[num] || num;
  };

  if (runsLoading || restrictedLoading || configsLoading) return <Loader />;

  const runs = runsData?.getPayrollRuns?.payrollRuns || [];
  const restrictedDays =
    restrictedData?.getRestrictedDays?.restrictedDays || [];
  const configs = configsData?.getSystemConfigs?.configs || [];
  const employees = empData?.getEmployeelist?.employee || [];
  const payouts = payoutsData?.getEmployeePayouts?.payouts || [];

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <Layout>
        <div className="p-6 bg-white w-full rounded-lg shadow-md m-4 overflow-y-auto">
          {/* Header */}
          <div className="flex justify-between mb-6 border-b pb-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                Payroll & Payout Operations
              </h1>
              <p className="text-sm text-gray-500">
                Generate monthly runs, manage restricted days, and config
                payroll details
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  if (employees.length > 0) {
                    setAdjustmentEmployeeId(employees[0].id);
                  }
                  setShowAdjustmentModal(true);
                }}
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-4 py-2 rounded shadow-sm text-sm font-semibold transition"
              >
                Add Manual Adjustment
              </button>
              <button
                onClick={() => setShowRunModal(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded shadow-sm text-sm font-semibold transition"
              >
                Generate Payroll Run
              </button>
            </div>
          </div>

          {/* Tabs Navigation */}
          <div className="flex border-b mb-6">
            <button
              onClick={() => setActiveTab("runs")}
              className={`py-2 px-4 font-semibold text-sm border-b-2 transition ${
                activeTab === "runs"
                  ? "border-[#FBB033] text-[#FBB033]"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Payroll Runs
            </button>
            <button
              onClick={() => setActiveTab("restricted")}
              className={`py-2 px-4 font-semibold text-sm border-b-2 transition ${
                activeTab === "restricted"
                  ? "border-[#FBB033] text-[#FBB033]"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Restricted Days
            </button>
            <button
              onClick={() => setActiveTab("configs")}
              className={`py-2 px-4 font-semibold text-sm border-b-2 transition ${
                activeTab === "configs"
                  ? "border-[#FBB033] text-[#FBB033]"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              System Configurations
            </button>
            <button
              onClick={() => setActiveTab("reports")}
              className={`py-2 px-4 font-semibold text-sm border-b-2 transition ${
                activeTab === "reports"
                  ? "border-[#FBB033] text-[#FBB033]"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Reports
            </button>
            <button
              onClick={() => setActiveTab("fullFinal")}
              className={`py-2 px-4 font-semibold text-sm border-b-2 transition ${
                activeTab === "fullFinal"
                  ? "border-[#FBB033] text-[#FBB033]"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Full &amp; Final
            </button>
          </div>

          {activeTab === "reports" && <PayrollTrends />}
          {activeTab === "fullFinal" && <FullFinalList />}

          {/* Payroll Runs View */}
          {activeTab === "runs" && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm text-gray-600">
                <thead className="bg-gray-50 text-gray-700 font-bold border-b">
                  <tr>
                    <th className="py-3 px-4">Period</th>
                    <th className="py-3 px-4">Working Days</th>
                    <th className="py-3 px-4">Status</th>
                    <th className="py-3 px-4">Created On</th>
                    <th className="py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {runs.length === 0 ? (
                    <tr>
                      <td
                        colSpan="5"
                        className="text-center py-6 text-gray-400"
                      >
                        No payroll runs generated yet. Click "Generate Payroll
                        Run" to start.
                      </td>
                    </tr>
                  ) : (
                    runs.map((run) => (
                      <tr key={run.id} className="hover:bg-gray-50 transition">
                        <td className="py-3 px-4 font-semibold text-gray-800">
                          {getMonthName(run.month)} {run.year}
                        </td>
                        <td className="py-3 px-4 text-gray-700 font-medium">
                          {run.expectedWorkingDays} days
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`px-2 py-1 rounded text-xs font-bold uppercase ${
                              run.status === "DRAFT"
                                ? "bg-yellow-100 text-yellow-800"
                                : run.status === "LOCKED"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-green-100 text-green-800"
                            }`}
                          >
                            {run.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-xs text-gray-500">
                          {formatDateTime(run.createdAt)}
                        </td>
                        <td className="py-3 px-4 space-x-2">
                          <button
                            onClick={() => {
                              setSelectedRun(run);
                              setShowPayoutsModal(true);
                            }}
                            className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-3 py-1 rounded text-xs font-semibold transition"
                          >
                            View Payouts
                          </button>

                          {run.status === "DRAFT" && (
                            <>
                              <button
                                onClick={async () => {
                                  try {
                                    const res = await generatePayroll({
                                      variables: {
                                        month: run.month,
                                        year: run.year,
                                        expectedWorkingDays:
                                          run.expectedWorkingDays,
                                      },
                                    });
                                    alert(res.data.generatePayroll.message);
                                    refetchRuns();
                                    if (selectedRun?.id) refetchPayouts();
                                  } catch (err) {
                                    alert(err.message);
                                  }
                                }}
                                className="bg-orange-50 hover:bg-orange-100 text-[#FBB033] px-3 py-1 rounded text-xs font-semibold transition"
                              >
                                Recalculate
                              </button>
                              <button
                                onClick={() => setCheckFor({ run, lock: true })}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs font-semibold transition"
                                title="Opens the payroll check; lock from there"
                              >
                                Check & Lock
                              </button>
                            </>
                          )}

                          {run.status !== "DRAFT" && (
                            <button
                              onClick={() => setCheckFor({ run, lock: false })}
                              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded text-xs font-semibold transition"
                            >
                              Check
                            </button>
                          )}
                          {run.status === "LOCKED" && (
                            <button
                              onClick={() => handleMarkPaid(run)}
                              className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs font-semibold transition"
                            >
                              Mark Paid
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Restricted Days View */}
          {activeTab === "restricted" && (
            <div className="grid grid-cols-3 gap-6">
              {/* Form card */}
              <div className="bg-gray-50 border p-4 rounded-lg self-start">
                <h3 className="font-bold text-gray-800 mb-3">
                  Add Special Day
                </h3>
                <form
                  onSubmit={handleAddRestrictedSubmit}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">
                      Type
                    </label>
                    <select
                      className="w-full border p-2 rounded text-sm bg-white"
                      value={restrictedKind}
                      onChange={(e) => setRestrictedKind(e.target.value)}
                    >
                      <option value="RESTRICTED">Restricted day (higher leave fine)</option>
                      <option value="NORMAL">Normal weekend (charge leave like a weekday)</option>
                    </select>
                    {restrictedKind === "NORMAL" && (
                      <p className="text-xs text-gray-500 mt-1">
                        For a quiet Saturday or Sunday: leave uses free leave first and gets the
                        weekday fine, not the weekend one.
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">
                      Date
                    </label>
                    <input
                      required
                      type="date"
                      className="w-full border p-2 rounded text-sm bg-white"
                      value={restrictedDate}
                      onChange={(e) => setRestrictedDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">
                      Name / Reason
                    </label>
                    <input
                      required
                      type="text"
                      placeholder={restrictedKind === "NORMAL" ? "e.g. Quiet weekend after Diwali" : "e.g. Diwali Peak Booking"}
                      className="w-full border p-2 rounded text-sm bg-white"
                      value={restrictedName}
                      onChange={(e) => setRestrictedName(e.target.value)}
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-[#FBB033] hover:bg-[#e09a2b] text-white py-2 rounded text-sm font-semibold transition"
                  >
                    {restrictedKind === "NORMAL" ? "Mark Weekend as Normal" : "Add Restricted Day"}
                  </button>
                </form>
              </div>

              {/* Table */}
              <div className="col-span-2 overflow-x-auto border rounded-lg">
                <div className="flex justify-end p-2 border-b bg-gray-50">
                  <button
                    onClick={() =>
                      setHistoryFor({
                        entityType: "RESTRICTED_DAY",
                        title: "Restricted & normal days change history",
                      })
                    }
                    className="bg-white border px-3 py-1 rounded shadow-sm hover:bg-gray-100 text-xs font-semibold text-gray-700"
                  >
                    History
                  </button>
                </div>
                <table className="min-w-full text-left text-sm text-gray-600">
                  <thead className="bg-gray-50 text-gray-700 font-bold border-b">
                    <tr>
                      <th className="py-3 px-4">Date</th>
                      <th className="py-3 px-4">Type</th>
                      <th className="py-3 px-4">Name / Reason</th>
                      <th className="py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {restrictedDays.length === 0 ? (
                      <tr>
                        <td
                          colSpan="4"
                          className="text-center py-6 text-gray-400"
                        >
                          No restricted/high-demand days defined.
                        </td>
                      </tr>
                    ) : (
                      restrictedDays.map((day) => (
                        <tr
                          key={day.id}
                          className="hover:bg-gray-50 transition"
                        >
                          <td className="py-3 px-4 font-semibold text-gray-800">
                            {day.date}
                          </td>
                          <td className="py-3 px-4">
                            {day.kind === "NORMAL" ? (
                              <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-800">
                                Normal weekend
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-800">
                                Restricted
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4">{day.name}</td>
                          <td className="py-3 px-4">
                            <button
                              onClick={() => handleRemoveRestricted(day.id)}
                              className="text-red-600 hover:text-red-800 text-xs font-semibold"
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* System Configurations View */}
          {activeTab === "configs" && (
            <>
            <OutOfHoursBonusEditor configs={configs} onSaved={refetchConfigs} />
            {/* No overflow clipping here: the ⓘ popovers extend past the table */}
            <div className="border rounded-lg">
              <div className="flex justify-between items-center p-4 border-b bg-gray-50">
                <h3 className="font-bold text-gray-800">
                  System Configurations
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    Hover ⓘ for what each setting does
                  </span>
                </h3>
                <div className="flex gap-2">
                <button
                  onClick={() => setShowAcks(true)}
                  className="bg-white border px-3 py-1.5 rounded shadow-sm hover:bg-gray-100 transition text-sm font-semibold text-gray-700"
                  title="Who has read pay-rule changes in the app"
                >
                  Acknowledgements
                </button>
                <button
                  onClick={() =>
                    setHistoryFor({ entityType: "SYSTEM_CONFIG", title: "Settings change history" })
                  }
                  className="bg-white border px-3 py-1.5 rounded shadow-sm hover:bg-gray-100 transition text-sm font-semibold text-gray-700"
                >
                  History
                </button>
                <button
                  onClick={() => {
                    setEditingConfig({ id: "new", key: "", valueJson: "" });
                    setEditingConfigValue("");
                  }}
                  className="bg-blue-600 text-white px-3 py-1.5 rounded shadow hover:bg-blue-700 transition text-sm font-semibold"
                >
                  + Add Config
                </button>
                </div>
              </div>
              <table className="min-w-full text-left text-sm text-gray-600">
                <thead className="bg-gray-50 text-gray-700 font-bold border-b">
                  <tr>
                    <th className="py-3 px-4">Configuration Key</th>
                    <th className="py-3 px-4">Value</th>
                    <th className="py-3 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {configs.map((config) => (
                    <tr key={config.id} className="hover:bg-gray-50 transition">
                      <td className="py-3 px-4 font-mono text-gray-800 text-xs">
                        <span className={config.inUse === false ? "text-gray-400" : ""}>
                          {config.key}
                        </span>
                        <ConfigInfo config={config} />
                        <div className="font-sans text-[11px] text-gray-400 mt-0.5">
                          {config.area}
                          {config.isDefault && " · default, not saved yet"}
                          {config.inUse === false && " · not used"}
                        </div>
                      </td>
                      <td className="py-3 px-4 font-mono text-sm text-gray-600 truncate max-w-xs">
                        {showConfigValue(config.valueJson)}
                        {config.unit && (
                          <span className="ml-1 font-sans text-xs text-gray-400">{config.unit}</span>
                        )}
                      </td>
                      <td className="py-3 px-4 space-x-3 whitespace-nowrap">
                        <button
                          onClick={() => {
                            setEditingConfig(config);
                            setEditingConfigValue(showConfigValue(config.valueJson));
                          }}
                          className="text-blue-600 hover:text-blue-800 text-xs font-semibold"
                        >
                          Edit
                        </button>
                        {WHAT_IF_KEYS.includes(config.key) && (
                          <button
                            onClick={() => setWhatIfFor(config)}
                            className="text-purple-600 hover:text-purple-800 text-xs font-semibold"
                            title="See what a different value would have cost last month"
                          >
                            What if
                          </button>
                        )}
                        <button
                          onClick={() =>
                            setHistoryFor({
                              entityType: "SYSTEM_CONFIG",
                              entityKey: config.key,
                              title: `History · ${config.key}`,
                            })
                          }
                          className="text-gray-600 hover:text-gray-900 text-xs font-semibold"
                        >
                          History
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      </Layout>

      {/* Generate payroll run modal */}
      {showRunModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-[400px] shadow-xl">
            <h2 className="text-xl font-bold mb-4 text-gray-800">
              Generate Payroll
            </h2>
            <form onSubmit={handleCreateRunSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">
                  Month
                </label>
                <select
                  className="w-full border p-2 rounded text-sm bg-white"
                  value={runMonth}
                  onChange={(e) => setRunMonth(e.target.value)}
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={m}>
                      {getMonthName(m)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">
                  Year
                </label>
                <input
                  required
                  type="number"
                  className="w-full border p-2 rounded text-sm bg-white"
                  value={runYear}
                  onChange={(e) => setRunYear(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">
                  Expected Working Days
                </label>
                <div className="w-full border p-2 rounded text-sm bg-gray-50 text-gray-700">
                  {expectedWorkingDays ?? "—"} days
                  <span className="block text-xs text-gray-500 mt-0.5">
                    Calculated automatically: days in the month minus Sundays
                  </span>
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowRunModal(false)}
                  className="px-4 py-2 border rounded text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition"
                >
                  Generate
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Payouts Modal */}
      {showPayoutsModal && selectedRun && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-[950px] shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 border-b pb-2">
              <h2 className="text-xl font-bold text-gray-800">
                Payout details for {getMonthName(selectedRun.month)}{" "}
                {selectedRun.year}
              </h2>
              {["LOCKED", "PAID"].includes(selectedRun.status) && (
                <button
                  onClick={() => setShowAddMissed(true)}
                  className="ml-auto text-sm font-semibold text-blue-600 hover:text-blue-800"
                  title="Someone left out of this locked month"
                >
                  + Add missed professional
                </button>
              )}
              <button
                onClick={() => downloadRegister(selectedRun, payouts)}
                disabled={!payouts.length}
                className={`${["LOCKED", "PAID"].includes(selectedRun.status) ? "ml-4" : "ml-auto"} mr-3 text-sm font-semibold text-blue-600 hover:text-blue-800 disabled:opacity-40`}
                title="One row per professional, for accounts"
              >
                Download register (CSV)
              </button>
              <span
                className={`px-2 py-0.5 rounded text-xs font-bold uppercase ${
                  selectedRun.status === "DRAFT"
                    ? "bg-yellow-100 text-yellow-800"
                    : selectedRun.status === "LOCKED"
                      ? "bg-blue-100 text-blue-800"
                      : "bg-green-100 text-green-800"
                }`}
              >
                {selectedRun.status}
              </span>
            </div>

            <div className="overflow-x-auto mb-6">
              <table className="min-w-full text-left text-sm text-gray-600">
                <thead className="bg-gray-50 text-gray-700 font-bold border-b">
                  <tr>
                    <th className="py-2 px-3">Employee</th>
                    <th className="py-2 px-3">Base Salary</th>
                    <th className="py-2 px-3">Commission</th>
                    <th className="py-2 px-3">Travel Allowance</th>
                    <th className="py-2 px-3">Leave Penalty</th>
                    <th className="py-2 px-3">Adjustments</th>
                    <th className="py-2 px-3">Net Payout</th>
                    <th className="py-2 px-3">Payment</th>
                    <th className="py-2 px-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {payouts.length === 0 ? (
                    <tr>
                      <td
                        colSpan="9"
                        className="text-center py-6 text-gray-400"
                      >
                        No payouts calculated yet for this month.
                      </td>
                    </tr>
                  ) : (
                    payouts.map((p) => (
                      <tr key={p.id} className="hover:bg-gray-50 transition">
                        <td className="py-2 px-3">
                          <div className="font-semibold text-gray-800">
                            {p.employee?.fullName}
                          </div>
                          <div className="text-xs text-gray-400">
                            {p.employee?.contactNumber}
                          </div>
                          {p.supplementary && (
                            <span
                              title={p.supplementaryReason || ""}
                              className="inline-block mt-0.5 px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 text-[10px] font-semibold"
                            >
                              Added after lock
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-3">₹{p.baseSalary}</td>
                        <td className="py-2 px-3">
                          {/* Travel allowance has its own column; this is
                              booking commission + out-of-hours bonus */}
                          <span className="text-green-600 font-medium mr-2">
                            +₹{(p.commission || 0) - (p.travelCommission || 0)}
                          </span>
                          <button
                            onClick={() => {
                              setSelectedCommissionEmployee(p.employee);
                              getMonthCommissions({
                                variables: {
                                  employeeId: p.employeeId,
                                  month: selectedRun.month,
                                  year: selectedRun.year,
                                },
                              });
                              setShowCommissionsModal(true);
                            }}
                            className="text-xs text-blue-500 hover:underline"
                          >
                            Details
                          </button>
                        </td>
                        <td className="py-2 px-3">
                          {p.travelCommission > 0 ? (
                            <span className="text-green-600 font-medium">+₹{p.travelCommission}</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="py-2 px-3">
                          <span className="text-red-600 font-medium mr-2">
                            -₹{p.leaveDeduction}
                          </span>
                          <button
                            onClick={() => {
                              setSelectedPenaltyEmployee(p.employee);
                              getEmployeePenalties({
                                variables: {
                                  employeeId: p.employeeId,
                                  month: selectedRun.month,
                                  year: selectedRun.year,
                                },
                              });
                              setShowPenaltyModal(true);
                            }}
                            className="text-xs text-blue-500 hover:underline"
                          >
                            Details
                          </button>
                        </td>
                        <td className="py-2 px-3">
                          <span
                            className={
                              p.adjustmentTotal >= 0
                                ? "text-green-600 font-medium mr-2"
                                : "text-red-600 font-medium mr-2"
                            }
                          >
                            {p.adjustmentTotal >= 0 ? "+" : ""}₹
                            {p.adjustmentTotal}
                          </span>
                          {p.adjustments?.length > 0 && (
                            <button
                              onClick={() => {
                                setSelectedAdjustmentEmployee(p.employee);
                                setSelectedAdjustments(p.adjustments);
                                setShowAdjustmentsModal(true);
                              }}
                              className="text-xs text-blue-500 hover:underline"
                            >
                              Details
                            </button>
                          )}
                        </td>
                        <td className="py-2 px-3 font-bold text-gray-800">
                          ₹{p.netPayout}
                          {p.carriedForward > 0 && (
                            <div className="text-[11px] font-normal text-red-600">
                              ₹{p.carriedForward} to next month
                            </div>
                          )}
                        </td>
                        <td className="py-2 px-3 text-xs">
                          {p.status === "PAID" ? (
                            <div>
                              <span className="px-2 py-0.5 rounded bg-green-100 text-green-800 font-semibold">Paid</span>
                              <div className="text-gray-500 mt-1">
                                {p.paidAt ? new Date(p.paidAt).toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata" }) : ""}
                                {p.paymentMode ? ` · ${p.paymentMode}` : ""}
                              </div>
                              {p.paymentReference && (
                                <div className="text-gray-500 font-mono break-all">{p.paymentReference}</div>
                              )}
                              {p.cashAdjusted > 0 && (
                                <div className="text-gray-500">
                                  cash ₹{p.cashAdjusted} set off · sent ₹{p.transferAmount}
                                </div>
                              )}
                            </div>
                          ) : p.supplementary && p.status === "DRAFT" ? (
                            <span className="px-2 py-0.5 rounded bg-yellow-100 text-yellow-800 font-semibold">
                              Waiting for approval
                            </span>
                          ) : selectedRun.status === "LOCKED" ? (
                            <button
                              onClick={() => {
                                setPayRef("");
                                setPayMode("BANK_TRANSFER");
                                setAdjustCash(false);
                                loadCash({ variables: { employeeId: p.employeeId } });
                                setPayingFor({ kind: "payout", payout: p });
                              }}
                              className="bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded font-semibold"
                            >
                              Mark paid
                            </button>
                          ) : (
                            <span className="text-gray-400">{selectedRun.status === "DRAFT" ? "Lock first" : "-"}</span>
                          )}
                        </td>
                        <td className="py-2 px-3">
                          <button
                            onClick={() => {
                              setAdjustmentEmployeeId(p.employeeId);
                              setShowAdjustmentModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 text-xs font-semibold"
                          >
                            + Adjustment
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowPayoutsModal(false);
                  setSelectedRun(null);
                }}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded text-sm transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Config Modal */}
      {showAcks && <RuleAcknowledgementsModal onClose={() => setShowAcks(false)} />}

      {checkFor && (
        <PayrollCheckModal
          month={checkFor.run.month}
          year={checkFor.run.year}
          runId={checkFor.run.id}
          canLock={checkFor.lock}
          onChanged={() => refetchRuns()}
          onClose={() => setCheckFor(null)}
        />
      )}

      {showAddMissed && selectedRun && (
        <AddSupplementaryModal
          run={selectedRun}
          monthLabel={`${getMonthName(selectedRun.month)} ${selectedRun.year}`}
          onClose={() => setShowAddMissed(false)}
          onAdded={() => {
            setShowAddMissed(false);
            // A paid month has someone unpaid again
            setSelectedRun({ ...selectedRun, status: "LOCKED" });
            refetchPayouts();
            refetchRuns();
          }}
        />
      )}

      {whatIfFor && <WhatIfModal config={whatIfFor} onClose={() => setWhatIfFor(null)} />}

      {payingFor && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-1">
              {payingFor.kind === "run"
                ? `Mark ${getMonthName(payingFor.run.month)} ${payingFor.run.year} paid`
                : `Mark ${payingFor.payout.employee?.fullName || ""} paid · ₹${payingFor.payout.netPayout}`}
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              {payingFor.kind === "run"
                ? "Everyone not yet marked paid gets this reference. Use it for a bulk transfer; otherwise mark people one by one in the payout details."
                : "Proof of payment, shown to the professional on their payslip."}
            </p>
            {payingFor.kind === "payout" ? (
              <div className="mb-3 rounded border bg-gray-50 p-3 text-sm">
                {cashLoading ? (
                  <span className="text-gray-500">Checking booking cash in hand...</span>
                ) : cashInHand && cashInHand.amount > 0 ? (
                  <>
                    <label className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={adjustCash}
                        onChange={(e) => setAdjustCash(e.target.checked)}
                      />
                      <span>
                        Set the booking cash they hold (<b>₹{cashInHand.amount}</b>, {cashInHand.bookings.length}{" "}
                        bookings) against this pay
                      </span>
                    </label>
                    {adjustCash && (
                      <div className="mt-2 text-gray-700">
                        Transfer <b>₹{Math.max(0, payingFor.payout.netPayout - cashInHand.amount)}</b> · all{" "}
                        {cashInHand.bookings.length} bookings get settled
                        {cashInHand.amount > payingFor.payout.netPayout && (
                          <div className="text-red-700">
                            Cash is ₹{cashInHand.amount - payingFor.payout.netPayout} more than the pay - that is
                            deducted from this month's pay.
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <span className="text-gray-500">No booking cash in hand.</span>
                )}
              </div>
            ) : (
              <label className="flex items-start gap-2 mb-3 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={adjustCash}
                  onChange={(e) => setAdjustCash(e.target.checked)}
                />
                <span>Set each person's booking cash in hand against their pay (their cash bookings get settled)</span>
              </label>
            )}
            <label className="block text-sm text-gray-700 mb-3">
              Mode
              <select
                value={payMode}
                onChange={(e) => setPayMode(e.target.value)}
                className="block border rounded px-2 py-1 mt-1 w-full"
              >
                {PAYMENT_MODES.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm text-gray-700">
              Reference (UTR / transaction id){payMode === "CASH" ? " - optional" : ""}
              <input
                value={payRef}
                onChange={(e) => setPayRef(e.target.value)}
                className="block border rounded px-2 py-1 mt-1 w-full font-mono"
                autoFocus
              />
            </label>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setPayingFor(null)} className="px-4 py-2 rounded border text-sm font-semibold text-gray-700">
                Cancel
              </button>
              <button
                onClick={submitPayment}
                disabled={
                  payMode !== "CASH" &&
                  !payRef.trim() &&
                  !(
                    payingFor.kind === "payout" &&
                    adjustCash &&
                    cashInHand &&
                    cashInHand.amount >= payingFor.payout.netPayout
                  )
                }
                className="px-4 py-2 rounded bg-green-600 hover:bg-green-700 text-white text-sm font-semibold disabled:opacity-50"
              >
                Mark paid
              </button>
            </div>
          </div>
        </div>
      )}

      {historyFor && (
        <SettingsHistoryModal
          entityType={historyFor.entityType}
          entityKey={historyFor.entityKey}
          title={historyFor.title}
          onClose={() => setHistoryFor(null)}
        />
      )}

      {editingConfig && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-[450px] shadow-xl">
            <h2 className="text-xl font-bold mb-2 text-gray-800">
              {editingConfig.id === "new"
                ? "Add Configuration"
                : "Edit Configuration"}
            </h2>
            {editingConfig.id === "new" ? (
              <input
                required
                type="text"
                placeholder="ENTER_CONFIG_KEY"
                className="w-full border p-2 rounded text-sm bg-white font-mono mb-4 uppercase"
                value={editingConfig.key}
                onChange={(e) =>
                  setEditingConfig({
                    ...editingConfig,
                    key: e.target.value.toUpperCase(),
                  })
                }
              />
            ) : (
              <div className="mb-4">
                <p className="text-xs text-gray-400 font-mono">{editingConfig.key}</p>
                {editingConfig.description && (
                  <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                    {editingConfig.description}
                    {editingConfig.unit && <span className="text-gray-400"> ({editingConfig.unit})</span>}
                  </p>
                )}
              </div>
            )}
            <form onSubmit={handleUpdateConfigSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">
                  Value (JSON format)
                </label>
                <textarea
                  required
                  rows="4"
                  className="w-full border p-2 rounded text-sm bg-white font-mono"
                  value={editingConfigValue}
                  onChange={(e) => setEditingConfigValue(e.target.value)}
                />
                <span className="text-[10px] text-gray-400">
                  Ensure this is valid JSON (e.g. 500, "Sunday", true, etc.)
                </span>
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setEditingConfig(null)}
                  className="px-4 py-2 border rounded text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#FBB033] text-white rounded text-sm hover:bg-[#e09a2b] transition"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Adjustment Modal */}
      {showAdjustmentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-[400px] shadow-xl">
            <h2 className="text-xl font-bold mb-4 text-gray-800">
              Add Salary Adjustment
            </h2>
            <form onSubmit={handleAddAdjustmentSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">
                  Employee
                </label>
                <select
                  required
                  className="w-full border p-2 rounded text-sm bg-white"
                  value={adjustmentEmployeeId}
                  onChange={(e) => setAdjustmentEmployeeId(e.target.value)}
                >
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.fullName} ({emp.contactNumber})
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">
                    Type
                  </label>
                  <select
                    className="w-full border p-2 rounded text-sm bg-white"
                    value={adjustmentType}
                    onChange={(e) => {
                      setAdjustmentType(e.target.value);
                      setAdjustmentSub("");
                    }}
                  >
                    <option value="BONUS">Bonus</option>
                    <option value="MANUAL_DEDUCTION">
                      Manual Deduction (Penalty)
                    </option>
                    <option value="INCENTIVE">Incentive (Reimbursement)</option>
                    <option value="TOOLS_CHARGES">Tools Charges</option>
                    <option value="ADVANCE_PAYMENT">Advance Payment</option>
                    <option value="TRAINING_CHARGES">Training Charges</option>
                    <option value="SOP_DEDUCTION">SOP Deduction</option>
                    <option value="FEEDBACK_PENALTY">Feedback Penalty</option>
                  </select>
                </div>
                {subTyped && (
                  <div className="col-span-2 grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">
                        {subTyped.label} for
                      </label>
                      <select
                        required
                        className="w-full border p-2 rounded text-sm bg-white"
                        value={adjustmentSub}
                        onChange={(e) => setAdjustmentSub(e.target.value)}
                      >
                        <option value="">Pick one</option>
                        {subTyped.subCategories.map((sc) => (
                          <option key={sc.key} value={sc.key}>
                            {sc.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">
                        Booking ID (optional)
                      </label>
                      <input
                        className="w-full border p-2 rounded text-sm bg-white"
                        placeholder="e.g. BRB1234"
                        value={adjustmentBooking}
                        onChange={(e) => setAdjustmentBooking(e.target.value)}
                      />
                    </div>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">
                    Date (Optional)
                  </label>
                  <input
                    type="date"
                    className="w-full border p-2 rounded text-sm bg-white"
                    value={adjustmentDate}
                    onChange={(e) => setAdjustmentDate(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">
                    Amount (₹)
                  </label>
                  <input
                    required
                    type="number"
                    step="0.01"
                    placeholder="e.g. 500"
                    className="w-full border p-2 rounded text-sm bg-white"
                    value={adjustmentAmount}
                    onChange={(e) => setAdjustmentAmount(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase text-gray-500 mb-1">
                  Reason / Description
                </label>
                <textarea
                  required
                  placeholder="e.g. Festival Performance Bonus"
                  className="w-full border p-2 rounded text-sm bg-white"
                  value={adjustmentReason}
                  onChange={(e) => setAdjustmentReason(e.target.value)}
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowAdjustmentModal(false)}
                  className="px-4 py-2 border rounded text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Penalty Breakdown Modal */}
      {showPenaltyModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-[700px] shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 border-b pb-2">
              <h2 className="text-xl font-bold text-gray-800">
                Leave Penalty Breakdown - {selectedPenaltyEmployee?.fullName}
              </h2>
              <button
                onClick={() => setShowPenaltyModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {penaltiesLoading ? (
              <p className="text-gray-500 text-center py-4">
                Loading penalties...
              </p>
            ) : penaltiesData?.getEmployeePenalties?.penalties?.length > 0 ? (
              <table className="min-w-full text-left text-sm text-gray-600 mb-4">
                <thead className="bg-gray-50 text-gray-700 font-bold border-b">
                  <tr>
                    <th className="py-2 px-3">Date</th>
                    <th className="py-2 px-3">Reason</th>
                    <th className="py-2 px-3">Day's wage</th>
                    <th className="py-2 px-3">Fine</th>
                    <th className="py-2 px-3">Deducted</th>
                    <th className="py-2 px-3">Status / Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {penaltiesData.getEmployeePenalties.penalties.map((pen) => (
                    <tr
                      key={pen.id}
                      className={
                        pen.status === "WARNING"
                          ? "bg-orange-50"
                          : pen.status === "FINE_WAIVED"
                            ? "bg-yellow-50"
                            : "hover:bg-gray-50"
                      }
                    >
                      <td className="py-2 px-3">{pen.date}</td>
                      <td className="py-2 px-3">{pen.reason || "-"}</td>
                      {(() => {
                        // A day's row = unpaid day's wage + fine. Waive fine
                        // only keeps the wage; Warning drops both.
                        const wage = pen.dayCharge || 0;
                        const fine = pen.penaltyAmount - wage;
                        const deducted =
                          pen.status === "WARNING" ? 0
                            : pen.status === "FINE_WAIVED" ? wage
                            : pen.penaltyAmount;
                        const cut = "line-through text-gray-400";
                        return (
                          <>
                            <td className={`py-2 px-3 ${pen.status === "WARNING" ? cut : ""}`}>
                              {wage ? `₹${wage}` : "-"}
                            </td>
                            <td className={`py-2 px-3 ${pen.status !== "IMPOSED" ? cut : ""}`}>
                              {fine ? `₹${fine}` : "-"}
                            </td>
                            <td className="py-2 px-3 font-semibold text-red-600">₹{deducted}</td>
                          </>
                        );
                      })()}
                      <td className="py-2 px-3">
                        <select
                          value={pen.status}
                          disabled={selectedRun?.status !== "DRAFT"}
                          onChange={async (e) => {
                            const newStatus = e.target.value;
                            try {
                              const res = await updateLeavePenaltyStatus({
                                variables: { id: pen.id, status: newStatus },
                              });
                              if (
                                res.data.updateLeavePenaltyStatus.status ===
                                "SUCCESS"
                              ) {
                                refetchPenalties();
                                // The draft is recomputed on every load
                                if (selectedRun?.id) refetchPayouts();
                              } else {
                                alert(
                                  res.data.updateLeavePenaltyStatus.message,
                                );
                              }
                            } catch (err) {
                              alert(err.message);
                            }
                          }}
                          className={`text-xs font-semibold p-1 border rounded ${
                            pen.status === "WARNING"
                              ? "bg-orange-100 text-orange-800 border-orange-300"
                              : pen.status === "FINE_WAIVED"
                                ? "bg-yellow-100 text-yellow-800 border-yellow-300"
                                : "bg-red-100 text-red-800 border-red-300"
                          } ${selectedRun?.status !== "DRAFT" ? "opacity-70 cursor-not-allowed" : ""}`}
                        >
                          <option value="IMPOSED">Impose (wage + fine)</option>
                          <option value="FINE_WAIVED" disabled={!pen.dayCharge}>
                            Waive fine only
                          </option>
                          <option value="WARNING">Warning (waive all)</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-gray-500 text-center py-4">
                No leave penalties found for this month.
              </p>
            )}

            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowPenaltyModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded text-sm hover:bg-gray-300 transition"
              >
                Close
              </button>
            </div>
            {selectedRun?.status === "DRAFT" &&
              penaltiesData?.getEmployeePenalties?.penalties?.length > 0 && (
                <p className="text-xs text-gray-500 mt-2 text-right">
                  Note: Don't forget to click "Recalculate" on the draft run to
                  update the payout.
                </p>
              )}
          </div>
        </div>
      )}

      {/* Adjustments Details Modal */}
      {showAdjustmentsModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-[700px] shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 border-b pb-2">
              <h2 className="text-xl font-bold text-gray-800">
                Adjustments Breakdown - {selectedAdjustmentEmployee?.fullName}
              </h2>
              <button
                onClick={() => setShowAdjustmentsModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {selectedAdjustments.length > 0 ? (
              <table className="min-w-full text-left text-sm text-gray-600 mb-4">
                <thead className="bg-gray-50 text-gray-700 font-bold border-b">
                  <tr>
                    <th className="py-2 px-3">Date</th>
                    <th className="py-2 px-3">Type</th>
                    <th className="py-2 px-3">Description</th>
                    <th className="py-2 px-3">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {selectedAdjustments.map((adj) => (
                    <tr key={adj.id} className="hover:bg-gray-50">
                      <td className="py-2 px-3">{adj.date ? formatDate(adj.date) : formatDate(adj.createdAt)}</td>
                      <td className="py-2 px-3">
                        <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-semibold">
                          {adj.type}
                        </span>
                        {adj.subCategory && (
                          <div className="text-[11px] text-gray-500 mt-1">
                            {(categoriesData?.getAdjustmentCategories || [])
                              .find((c) => c.type === adj.type)
                              ?.subCategories.find((sc) => sc.key === adj.subCategory)?.label || adj.subCategory}
                          </div>
                        )}
                      </td>
                      <td className="py-2 px-3">{adj.description || "-"}</td>
                      <td className={`py-2 px-3 font-semibold ${adj.adjustmentDirection === 'CREDIT' ? 'text-green-600' : 'text-red-600'}`}>
                        {adj.adjustmentDirection === 'CREDIT' ? '+' : '-'}₹{adj.amount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-gray-500 text-center py-4">
                No adjustments found.
              </p>
            )}

            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowAdjustmentsModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded text-sm hover:bg-gray-300 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {viewingBooking && (
        <OrderDetailsModal booking={viewingBooking} onClose={() => setViewingBooking(null)} />
      )}

      {/* Commissions Details Modal */}
      {showCommissionsModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-[850px] shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4 border-b pb-2">
              <h2 className="text-xl font-bold text-gray-800">
                Commission &amp; Travel Breakdown - {selectedCommissionEmployee?.fullName}
              </h2>
              <button
                onClick={() => setShowCommissionsModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            {bookingsLoading ? (
              <p className="text-gray-500 text-center py-4">
                Loading commission orders...
              </p>
            ) : commissionRows.length > 0 ? (
              <table className="min-w-full text-left text-sm text-gray-600 mb-4">
                <thead className="bg-gray-50 text-gray-700 font-bold border-b">
                  <tr>
                    <th className="py-2 px-3">Booking ID</th>
                    <th className="py-2 px-3">Date</th>
                    <th className="py-2 px-3">Order Value</th>
                    <th className="py-2 px-3 text-right">Commission</th>
                    <th className="py-2 px-3 text-right">Out-of-hours</th>
                    <th className="py-2 px-3 text-right">Travel Allowance</th>
                    <th className="py-2 px-3 text-right">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {commissionRows.map((order) => (
                    <tr key={order.orderId} className="hover:bg-gray-50">
                      <td className="py-2 px-3 font-semibold">
                        <button
                          type="button"
                          onClick={() => openBooking(order.orderId, order.bookingId)}
                          disabled={openingBookingId === order.orderId}
                          title="Open booking details"
                          className="text-blue-600 hover:underline disabled:opacity-60"
                        >
                          {openingBookingId === order.orderId ? "Opening…" : order.bookingId}
                        </button>
                      </td>
                      <td className="py-2 px-3">{order.date}</td>
                      <td className="py-2 px-3">₹{order.price}</td>
                      <td className="py-2 px-3 text-right">₹{order.baseCommission}</td>
                      <td className="py-2 px-3 text-right">₹{order.bonusCommission}</td>
                      <td className="py-2 px-3 text-right">₹{order.travelCommission}</td>
                      <td className="py-2 px-3 font-bold text-green-600 text-right">+₹{order.totalCommission}</td>
                    </tr>
                  ))}
                  <tr className="font-bold text-gray-800">
                    <td className="py-2 px-3" colSpan={3}>Total ({commissionRows.length} bookings)</td>
                    {["baseCommission", "bonusCommission", "travelCommission", "totalCommission"].map((k) => (
                      <td key={k} className="py-2 px-3 text-right">
                        ₹{commissionRows.reduce((sum, o) => sum + (o[k] || 0), 0)}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            ) : (
              <p className="text-gray-500 text-center py-4">
                No commission orders found for this month.
              </p>
            )}

            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowCommissionsModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded text-sm hover:bg-gray-300 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayoutDashboard;
